import { Link } from "react-router";

import TriggeredByFlows from "components/TriggeredByFlows";
import { RUN_TRIGGER_TYPES } from "constants/run";
import { VcsProvider } from "types/generated";
import { isFlowsTrigger } from "utils/flows";

import styles from "./styles.module.css";

type TriggerByInfoProps = {
  stackId: string;
  triggeredBy?: string;
  driftDetection?: boolean;
  hidePrefix?: boolean;
  provider: VcsProvider;
};

enum RUN_TRIGGERED_BY {
  DRIFT_DETECTION = "DRIFT_DETECTION",
  IN_RESPONSE_TO_DETECTED_DRIFT = "IN_RESPONSE_TO_DETECTED_DRIFT",
  GIT_COMMIT = "GIT_COMMIT",
  POLICY = "POLICY",
  DEPENDENCY = "DEPENDENCY",
  MODULE = "MODULE",
  FLOWS = "FLOWS",
  USER = "USER",
}

const getTriggeredBy = (
  triggeredBy: TriggerByInfoProps["triggeredBy"],
  driftDetection: TriggerByInfoProps["driftDetection"]
) => {
  if (!triggeredBy && driftDetection) {
    return RUN_TRIGGERED_BY.DRIFT_DETECTION;
  }

  if (!triggeredBy || triggeredBy === RUN_TRIGGER_TYPES.VCS_COMMIT) {
    return RUN_TRIGGERED_BY.GIT_COMMIT;
  }

  if (triggeredBy.startsWith(RUN_TRIGGER_TYPES.POLICY_PREFIX)) {
    return RUN_TRIGGERED_BY.POLICY;
  }

  if (triggeredBy.startsWith(RUN_TRIGGER_TYPES.MODULE_PREFIX)) {
    return RUN_TRIGGERED_BY.MODULE;
  }

  if (triggeredBy.startsWith(RUN_TRIGGER_TYPES.DEPENDENCY_PREFIX)) {
    return RUN_TRIGGERED_BY.DEPENDENCY;
  }

  if (triggeredBy.startsWith(RUN_TRIGGER_TYPES.RECONCILIATION_PREFIX)) {
    return RUN_TRIGGERED_BY.IN_RESPONSE_TO_DETECTED_DRIFT;
  }

  if (isFlowsTrigger(triggeredBy)) {
    return RUN_TRIGGERED_BY.FLOWS;
  }

  return RUN_TRIGGERED_BY.USER;
};

const TriggerByInfo = ({
  stackId,
  triggeredBy,
  driftDetection,
  hidePrefix,
  provider,
}: TriggerByInfoProps) => {
  const runTriggeredBy = getTriggeredBy(triggeredBy, driftDetection);
  const prefix = hidePrefix ? "" : "by ";

  if (runTriggeredBy === RUN_TRIGGERED_BY.DRIFT_DETECTION) {
    return <>{`${prefix}drift detection`}</>;
  }

  if (runTriggeredBy === RUN_TRIGGERED_BY.GIT_COMMIT) {
    const commitLabel = provider === VcsProvider.Spacelift ? "a Repo commit" : "a Git commit";
    return <>{`${prefix}${commitLabel}`}</>;
  }

  if (runTriggeredBy === RUN_TRIGGERED_BY.DEPENDENCY && triggeredBy) {
    const [, stackSlug] = triggeredBy.split("/");

    return (
      <Link to={`/stack/${stackSlug}`} className={styles.link}>
        {`${prefix}${triggeredBy}`}
      </Link>
    );
  }

  if (runTriggeredBy === RUN_TRIGGERED_BY.POLICY && triggeredBy) {
    const [, stackSlug, runId] = triggeredBy.split("/");

    return (
      <Link to={`/stack/${stackSlug}/run/${runId}`} className={styles.link}>
        {`${prefix}a trigger policy`}
      </Link>
    );
  }

  if (runTriggeredBy === RUN_TRIGGERED_BY.MODULE && triggeredBy) {
    const [, moduleSlug, versionId] = triggeredBy.split("/");

    return (
      <Link to={`/module/${moduleSlug}/version/${versionId}`} className={styles.link}>
        {`${prefix}a new module version release`}
      </Link>
    );
  }

  if (runTriggeredBy === RUN_TRIGGERED_BY.IN_RESPONSE_TO_DETECTED_DRIFT && triggeredBy) {
    const [, runId] = triggeredBy.split("/");

    return (
      <Link to={`/stack/${stackId}/run/${runId}`} className={styles.link}>
        in response to detected drift
      </Link>
    );
  }

  if (runTriggeredBy === RUN_TRIGGERED_BY.FLOWS && triggeredBy) {
    // The span keeps "by" and the link in one inline formatting context — as direct flex
    // items of a meta info list they would be two items, with the space between trimmed.
    return (
      <span>
        {prefix}
        <TriggeredByFlows triggeredBy={triggeredBy} />
      </span>
    );
  }

  return <>{`${prefix}${triggeredBy}`}</>;
};

export default TriggerByInfo;
